import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { Icon } from "@iconify/react";
import Page from "src/components/Page";
import { BaseTable } from "src/components/BaseTable/BaseTable";
import { teamBreakdownColumns } from "./payoutHistory.constants";
import { fetchWithError, fetchApi } from "src/react-query/fetchWithError";
import { selector } from "src/react-query/select";
import { queryKeys } from "src/react-query/queryKeys";
import { runPayoutScheduleForTeam } from "src/react-query/endPoints";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import enums from "src/util/enums";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useAuth } from "src/hooks/useAuth.hook";

const statusColorMap = {
  SUCCESS: "success",
  FAILED: "error",
  IN_PROGRESS: "warning",
};

const InfoRow = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight="medium">
      {value ?? "—"}
    </Typography>
  </Grid>
);

const PayoutRunDetail = () => {
  const { logId } = useParams();
  const team = useSelectedTeam();
  const companyId = team?.teamId;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { actions: { setAlert } } = alertSlice;
  const [rerunning, setRerunning] = useState(false);
  const user = useAuth();
  const companyType = useMemo(() => user?.user?.company?.companyType, [user]);


  const { data, isLoading } = useQuery(
    queryKeys.payoutRunDetail(null, companyId, logId),
    fetchWithError,
    {
      enabled: !!logId,
    }
  );
  const detail = selector(data);

  const teamBreakdown = useMemo(() => {
    if (!detail?.summaryJson?.teamBreakdown) return [];
    return detail.summaryJson.teamBreakdown.map((team, idx) => ({
      ...team,
      id: team.companyId || idx,
    }));
  }, [detail]);

  const handleRerun = async () => {
    if (!detail?.companyId) return;
    setRerunning(true);
    try {
      await fetchApi({
          url: runPayoutScheduleForTeam(detail.companyId),
          method: "POST",
        });
      
      dispatch(
        setAlert({
          open: true,
          message: "Payout re-run triggered successfully",
          severity: enums.SEVERITY.SUCCESS,
        })
      );
    } catch (err) {
      dispatch(
        setAlert({
          open: true,
          message: `Re-run failed: ${err?.response?.data?.message || err.message}`,
          severity: enums.SEVERITY.ERROR,
        })
      );
    } finally {
      setRerunning(false);
    }
  };

  if (isLoading) {
    return (
      <Page title="Payout Run Detail | Smart-CMS">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (!detail) {
    return (
      <Page title="Payout Run Detail | Smart-CMS">
        <Alert severity="warning">Run log not found.</Alert>
      </Page>
    );
  }

  return (
    <Page title="Payout Run Detail | Smart-CMS">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            size="small"
            startIcon={<Icon icon="eva:arrow-back-fill" />}
            onClick={() => navigate("/payout-history")}
          >
            Back
          </Button>
          <Typography variant="h4">
            Run #{detail.id}
          </Typography>
          <Chip
            label={detail.status}
            color={statusColorMap[detail.status] || "default"}
            size="small"
          />
          <Chip
            label={detail.runType}
            size="small"
            variant="outlined"
            color={detail.runType === "MANUAL" ? "info" : "default"}
          />
        </Stack>
        {companyType  === 'GP' && detail.status === "FAILED" && (
          <Button
            variant="contained"
            color="warning"
            onClick={handleRerun}
            disabled={rerunning}
            startIcon={rerunning ? <CircularProgress size={16} /> : <Icon icon="mdi:refresh" />}
          >
            Re-run Payout
          </Button>
        )}
      </Stack>

      {detail.status === "FAILED" && detail.errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {detail.errorMessage}
        </Alert>
      )}

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Run Details
        </Typography>
        <Grid container spacing={2}>
          <InfoRow label="Company ID" value={detail.companyId} />
          <InfoRow label="Parent Company ID" value={detail.parentCompanyId} />
          <InfoRow label="Schedule ID" value={detail.scheduleId} />
          <InfoRow label="Run Type" value={detail.runType} />
          <InfoRow label="Status" value={detail.status} />
          <InfoRow label="Started At" value={yearFirstDateTimeFormat(detail.startedAt)} />
          <InfoRow label="Completed At" value={yearFirstDateTimeFormat(detail.completedAt)} />
          <InfoRow label="Transactions Processed" value={detail.transactionsProcessed} />
          <InfoRow label="Skipped Teams" value={detail.skippedTeams} />
        </Grid>
      </Card>

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Financial Summary
        </Typography>
        <Grid container spacing={2}>
          <InfoRow
            label="Total Payout"
            value={Number(detail.totalPayoutAmount || 0).toFixed(2)}
          />
          {/* <InfoRow
            label="Total Payout (excl. Tax)"
            value={Number(detail.totalPayoutWithoutTax || 0).toFixed(2)}
          /> */}
          <InfoRow
            label="Zynka Commission"
            value={Number(detail.zynkaCommission || 0).toFixed(2)}
          />
        </Grid>
      </Card>

      {/* {teamBreakdown.length > 0 && (
        <Card sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
            Team Breakdown
          </Typography>
          <BaseTable
            rows={teamBreakdown}
            columns={teamBreakdownColumns}
            getRowId={(row) => row.id}
            pageSize={10}
            disableSelectionOnClick
          />
        </Card>
      )} */}
    </Page>
  );
};

export default PayoutRunDetail;
